// pages/index.js
import React, { useState } from "react";
import Head from "next/head";
import {
  Typography,
  Grid2 as Grid,
  Box,
  Button,
  Container,
  Card,
  CardContent,
  TextField,
  InputLabel,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import DefaultLayout from "../../layout/DefaultLayout";

// Yup schema for validation
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required"),
});

function Contact() {
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMsg(
          "Thank you for contacting me. I'll get back to you soon!"
        );
        reset();
      } else {
        setErrorMsg("Failed to send message. Please try again later.");
        console.error(result.error);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Head>
        <title>Contact | Valentin Marko</title>

        <meta
          name="description"
          content="Contact Valentin Marko. Send a message about a project, collaboration, or a question and I’ll reply as soon as possible."
        />

        <meta name="robots" content="index, follow" />

        <link rel="canonical" href="https://valentinmarko.com/contact" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact | Valentin Marko" />
        <meta
          property="og:description"
          content="Send Valentin Marko a message about a project, collaboration, or a question."
        />
        <meta property="og:url" content="https://valentinmarko.com/contact" />
        <meta
          property="og:site_name"
          content="Valentin Marko – Software Development Blog"
        />
        <meta
          property="og:image"
          content="https://valentinmarko.com/about.webp"
        />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact | Valentin Marko",
            url: "https://valentinmarko.com/contact",
            description:
              "Contact Valentin Marko about projects, collaborations, or questions.",
            isPartOf: { "@id": "https://valentinmarko.com/#website" },
            about: { "@id": "https://valentinmarko.com/#person" },
            mainEntity: {
              "@type": "Person",
              "@id": "https://valentinmarko.com/#person",
              name: "Valentin-Marko Cîmpan",
              email: "mailto:marko.cimpan.dev@gmail.com",
              telephone: "+40731115446",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Timișoara",
                addressCountry: "RO",
              },
              sameAs: ["https://www.linkedin.com/in/marko-cimpan/"],
            },
          }),
        }}
      />

      <Container sx={{ minHeight: "calc(100vh - 150px)" }}>
        <Grid container spacing={3} marginTop={"34px"} marginBottom={"34px"}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography
              component={"h1"}
              variant="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "25px", sm: "35px", lg: "44px" },
                fontWeight: 700,
                color: "#2D3748",
                textTransform: "capitalize",
              }}
            >
              Let’s Connect
            </Typography>
            <Typography
              component={"p"}
              variant="h5"
              sx={{
                fontSize: "20px",
                fontWeight: 400,
                color: "rgb(113, 128, 150)",
                marginBottom: "30px",
              }}
            >
              I’m a Full-Stack Developer specializing in modern web technologies
              like JavaScript, TypeScript, React, Next.js, Node.js, Nest.js and
              more. If you’d like to discuss a project, collaboration, or just
              want to say hi, feel free to reach out using the form below.
            </Typography>

            <Card sx={{ borderRadius: "15px" }}>
              <CardContent sx={{ padding: "30px" }}>
                {/* Success Message */}
                {successMsg && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(72, 187, 120, 0.1)",
                      padding: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      marginBottom: "16px",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "rgb(72, 187, 120)",
                      }}
                    >
                      {successMsg}
                    </Typography>
                  </Box>
                )}

                {/* Error Message */}
                {errorMsg && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(255, 77, 79, 0.1)", // red with same opacity as green
                      padding: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      marginBottom: "16px",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "rgb(255, 77, 79)",
                      }}
                    >
                      {errorMsg}
                    </Typography>
                  </Box>
                )}
                {/* <Box
                  sx={{
                    backgroundColor: "rgb(240, 255, 244)",
                    padding: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body2"
                    component={"p"}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "rgb(72, 187, 120)",
                    }}
                  >
                    Thank you for contacting us. We'll get back to you soon!
                  </Typography>
                </Box> */}
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2}>
                    {/* Name */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <InputLabel
                        error={!!errors.name}
                        htmlFor="name"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Name
                      </InputLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="name"
                            name={field.name}
                            autoComplete="name"
                            fullWidth
                            variant="outlined"
                            error={!!errors.name}
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.name ? "red" : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.name ? "red" : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.name ? "red" : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ fontSize: "14px" }}
                      >
                        {errors.name?.message}
                      </Typography>
                    </Grid>

                    {/* Company Name (no validation) */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <InputLabel
                        htmlFor="companyName"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Company Name
                      </InputLabel>
                      <Controller
                        name="companyName"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="companyName"
                            name={field.name}
                            autoComplete="organization"
                            fullWidth
                            variant="outlined"
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",

                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.companyName
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.companyName
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.companyName
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography variant="body2" sx={{ fontSize: "14px" }} />
                    </Grid>

                    {/* Email */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <InputLabel
                        error={!!errors.email}
                        htmlFor="email"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Email
                      </InputLabel>
                      <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="email"
                            name={field.name}
                            autoComplete="email"
                            fullWidth
                            variant="outlined"
                            placeholder="email@example.com"
                            error={!!errors.email}
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.email
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.email
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.email
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ fontSize: "14px" }}
                      >
                        {errors.email?.message}
                      </Typography>
                    </Grid>

                    {/* Phone Number (no validation) */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <InputLabel
                        htmlFor="phoneNumber"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Phone Number
                      </InputLabel>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="phoneNumber"
                            name={field.name}
                            autoComplete="tel"
                            fullWidth
                            variant="outlined"
                            placeholder="(xxxx) xxx-xxxx"
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.phoneNumber
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.phoneNumber
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.phoneNumber
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography variant="body2" sx={{ fontSize: "14px" }} />
                    </Grid>

                    {/* Subject */}
                    <Grid size={{ xs: 12 }}>
                      <InputLabel
                        error={!!errors.subject}
                        htmlFor="subject"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Subject
                      </InputLabel>
                      <Controller
                        name="subject"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="subject"
                            name={field.name}
                            autoComplete="subject"
                            fullWidth
                            variant="outlined"
                            error={!!errors.subject}
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.subject
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.subject
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.subject
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ fontSize: "14px" }}
                      >
                        {errors.subject?.message}
                      </Typography>
                    </Grid>

                    {/* Message */}
                    <Grid size={{ xs: 12 }}>
                      <InputLabel
                        error={!!errors.message}
                        htmlFor="message"
                        sx={{ color: "rgb(113, 128, 150)", mb: 1 }}
                      >
                        Your Message
                      </InputLabel>
                      <Controller
                        name="message"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="message"
                            name={field.name}
                            autoComplete="off"
                            fullWidth
                            multiline
                            rows={5}
                            variant="outlined"
                            error={!!errors.message}
                            sx={{
                              borderRadius: "4px",
                              backgroundColor: "rgb(226, 232, 240)",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: `1px solid ${
                                    !!errors.message
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&:hover fieldset": {
                                  border: `1px solid ${
                                    !!errors.message
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                                "&.Mui-focused fieldset": {
                                  border: `1px solid ${
                                    !!errors.message
                                      ? "red"
                                      : "rgb(226, 232, 240)"
                                  }`,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "rgb(113, 128, 150)",
                              },
                            }}
                          />
                        )}
                      />
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ fontSize: "14px" }}
                      >
                        {errors.message?.message}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    disabled={loading}
                    sx={{
                      mt: 2,
                      borderRadius: "30px",
                      padding: "8px 44px",
                      backgroundColor: loading ? "#7F8DA1" : "#667EEA",
                      color: "#ffffff",
                      textTransform: "none",
                      fontSize: "16px",
                      fontWeight: 400,
                      "&:hover": { backgroundColor: "#7F8DA1" },
                    }}
                  >
                    {loading ? "Sending..." : "Submit"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Right side: My Commitment & Other Ways */}
          <Grid size={{ xs: 4 }} sx={{ display: { xs: "none", md: "block" } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "24px" },
                fontWeight: 900,
                color: "rgb(113, 128, 158)",
                marginRight: "8px",
                fontWeight: 700,
                marginBottom: "34px",
              }}
            >
              {" "}
              My Commitment{" "}
            </Typography>{" "}
            <Card sx={{ borderRadius: "15px", marginBottom: "60px" }}>
              {" "}
              <CardContent sx={{ padding: "30px" }}>
                {" "}
                <Typography
                  sx={{
                    color: "rgb(113, 128, 150)",
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  {" "}
                  I take every message seriously and I will get back to you as
                  soon as possible. Whether you have a question about my
                  projects, need help with web development, or want to discuss
                  potential collaborations, I’m happy to connect.{" "}
                </Typography>{" "}
              </CardContent>{" "}
            </Card>{" "}
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "24px" },
                fontWeight: 900,
                color: "rgb(113, 128, 158)",
                marginRight: "8px",
                fontWeight: 700,
                marginBottom: "34px",
              }}
            >
              {" "}
              Other Ways to Reach Me{" "}
            </Typography>{" "}
            <Card sx={{ borderRadius: "15px", marginBottom: "14px" }}>
              {" "}
              <CardContent sx={{ padding: "30px" }}>
                {" "}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    marginBottom: "16px",
                    alignItems: "center",
                  }}
                >
                  {" "}
                  <EmailIcon
                    sx={{ color: "rgb(113, 128, 150)", fontSize: "30px" }}
                  />{" "}
                  <Typography
                    sx={{
                      color: "rgb(113, 128, 150)",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    {" "}
                    marko.cimpan.dev@gmail.com{" "}
                  </Typography>{" "}
                </Box>{" "}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    marginBottom: "16px",
                    alignItems: "center",
                  }}
                >
                  {" "}
                  <PhoneEnabledIcon
                    sx={{ color: "rgb(113, 128, 150)", fontSize: "30px" }}
                  />{" "}
                  <Typography
                    sx={{
                      color: "rgb(113, 128, 150)",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    {" "}
                    +40731115446{" "}
                  </Typography>{" "}
                </Box>{" "}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {" "}
                  <LocationOnIcon
                    sx={{ color: "rgb(113, 128, 150)", fontSize: "30px" }}
                  />{" "}
                  <Typography
                    sx={{
                      color: "rgb(113, 128, 150)",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    {" "}
                    Romania, Timisoara{" "}
                  </Typography>{" "}
                </Box>{" "}
              </CardContent>{" "}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

Contact.getLayout = (page) => <DefaultLayout>{page}</DefaultLayout>;

export default Contact;
